import React from "react";
import {
  Alert,
  Checkbox,
  Divider,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import LockIcon from "@mui/icons-material/Lock";
import Tooltip from "@mui/material/Tooltip";
import { parseDateOnly } from "../../shared/utils/dateUtils";

/**
 * Lista de fechas de limpieza pactada, con la marca de recambio de huésped.
 *
 * Vive en un componente propio porque el formulario de edición dibuja este
 * bloque en tres variantes de pantalla (escritorio, escritorio bajo y móvil).
 * Estaba copiado tres veces, así que un cambio aplicado en una sola de ellas
 * —como pasó al agregar el recambio— no se ve en las otras dos.
 */
export interface ListaLimpiezasPactadasProps {
  fechas: string[];
  fechasConRecambio: string[];
  /** Fechas que el personal ya marcó (hechas o no): son historia y no se pueden quitar. */
  fechasRegistradas?: string[];
  onQuitarFecha: (fecha: string) => void;
  onAlternarRecambio: (fecha: string, marcado: boolean) => void;
}

const formatoLargo = (fecha: Date) =>
  fecha.toLocaleDateString("es-AR", {
    weekday: "short",
    day: "2-digit",
    month: "2-digit",
  });

export const ListaLimpiezasPactadas: React.FC<ListaLimpiezasPactadasProps> = ({
  fechas,
  fechasConRecambio,
  fechasRegistradas = [],
  onQuitarFecha,
  onAlternarRecambio,
}) => {
  if (fechas.length === 0) {
    return (
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ fontStyle: "italic", mt: 1 }}
      >
        No hay fechas de limpieza agregadas. Usá el selector de fecha para agregar.
      </Typography>
    );
  }

  const hayDomingo = fechas.some((fecha) => parseDateOnly(fecha).getDay() === 0);

  return (
    <>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="baseline"
        sx={{ mt: 2, mb: 0.5 }}
      >
        <Typography variant="caption" color="text.secondary">
          Fechas de limpieza ({fechas.length}):
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Recambio
        </Typography>
      </Stack>

      <Stack divider={<Divider flexItem />}>
        {fechas.map((fecha) => {
          const fechaObj = parseDateOnly(fecha);
          const esDomingo = fechaObj.getDay() === 0;
          const registrada = fechasRegistradas.includes(fecha);
          const fechaCorta = fechaObj.toLocaleDateString("es-AR");
          return (
            <Stack
              key={fecha}
              direction="row"
              alignItems="center"
              spacing={1}
              sx={{ py: 0.25 }}
            >
              {registrada ? (
                <Tooltip title="El personal ya registró esta limpieza: no se puede quitar">
                  <span>
                    <IconButton size="small" disabled aria-label={`Limpieza del ${fechaCorta} ya registrada`}>
                      <LockIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              ) : (
                <IconButton
                  size="small"
                  aria-label={`Quitar limpieza del ${fechaCorta}`}
                  onClick={() => onQuitarFecha(fecha)}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              )}
              <Typography
                variant="body2"
                sx={{ flex: 1, textTransform: "capitalize" }}
                color={esDomingo ? "warning.main" : "text.primary"}
              >
                {formatoLargo(fechaObj)}
                {esDomingo ? " · domingo" : ""}
                {registrada ? " · registrada" : ""}
              </Typography>
              <Checkbox
                size="small"
                checked={fechasConRecambio.includes(fecha)}
                inputProps={{
                  "aria-label": `Recambio de huésped el ${fechaObj.toLocaleDateString("es-AR")}`,
                }}
                onChange={(e) => onAlternarRecambio(fecha, e.target.checked)}
              />
            </Stack>
          );
        })}
      </Stack>

      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
        Marcá recambio cuando ese día se va un huésped y entra otro. Es
        informativo: la limpieza se programa igual.
      </Typography>

      {hayDomingo && (
        <Alert severity="info" sx={{ mt: 1, fontSize: "0.8rem" }}>
          Hay una fecha que cae en domingo. Las fechas pactadas se respetan tal
          cual: verificá que haya personal disponible ese día.
        </Alert>
      )}
    </>
  );
};

export default ListaLimpiezasPactadas;
